import { useEffect, useState } from 'react';
import Footer from '../Shared/Footer/Footer';
import Header from '../Shared/Header/Header';
import CheckoutPage from '../Booking/BookingCheckout/CheckoutPage';
import PersonalInformation from '../Booking/PersonalInformation';
import { Button, Steps, message, Modal } from 'antd';
import moment from 'moment';
import SelectApppointment from './SelectApppointment';
import SelectDoctor from './SelectDoctor';
import useAuthCheck from '../../redux/hooks/useAuthCheck';
import { useCreateAppointmentMutation } from '../../redux/api/appointmentApi';
import { useDispatch } from 'react-redux';
import { addInvoice } from '../../redux/feature/invoiceSlice';
import { useNavigate } from 'react-router-dom';
import { visitReasonOptions } from '../../constant/global';
import { isCaptchaEnabled } from '../UI/TurnstileWidget';
import './AppointmentFlow.css';

const initialValue = {
  paymentMethod: 'offline',
  paymentType: 'payAtPractice',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  reasonForVisit: '',
  problemType: '',
  description: '',
  address: '',
};

const AppointmentPage = () => {
  const dispatch = useDispatch();
  const { data, role } = useAuthCheck();
  const [current, setCurrent] = useState(0);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectTime, setSelectTime] = useState('');
  const [isCheck, setIsChecked] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  const [selectValue, setSelectValue] = useState(initialValue);
  const [IsDisable, setIsDisable] = useState(true);
  const [isConfirmDisable, setIsConfirmDisable] = useState(true);
  const [patientId, setPatientId] = useState('');
  const navigate = useNavigate();

  const [createAppointment, { data: appointmentData, isError, isSuccess, isLoading, error }] =
    useCreateAppointmentMutation();

  const handleChange = (e) => setSelectValue({ ...selectValue, [e.target.name]: e.target.value });

  const next = () => setCurrent(current + 1);
  const prev = () => setCurrent(current - 1);

  useEffect(() => {
    const { firstName, lastName, email, phone, reasonForVisit } = selectValue;
    const isInputEmpty = !firstName || !lastName || !email || !phone || !reasonForVisit;
    setIsDisable(isInputEmpty);
    setIsConfirmDisable(!isCheck || (isCaptchaEnabled && !captchaToken));
  }, [selectValue, isCheck, captchaToken]);

  const handleConfirmSchedule = () => {
    const obj = {};
    const isPatientSession = role === 'patient' && data?.id;

    obj.patientInfo = {
      firstName: selectValue.firstName,
      lastName: selectValue.lastName,
      email: selectValue.email,
      phone: selectValue.phone,
      patientId: isPatientSession ? data.id : undefined,
      scheduleDate: selectedDate || undefined,
      scheduleTime: selectTime || 'anytime',
      reasonForVisit: [
        selectValue.problemType && visitReasonOptions.find((o) => o.value === selectValue.problemType)?.label,
        selectValue.reasonForVisit,
      ]
        .filter(Boolean)
        .join(' — ') || selectValue.reasonForVisit,
      description: selectValue.description || undefined,
      address: selectValue.address || undefined,
      ...(selectedDoctor?.id && { doctorId: selectedDoctor.id }),
    };
    obj.payment = {
      paymentType: selectValue.paymentType,
      paymentMethod: selectValue.paymentMethod,
    };
    obj.captchaToken = captchaToken || undefined;
    createAppointment(obj);
  };

  useEffect(() => {
    if (isSuccess && appointmentData?.id) {
      message.success('Booking request received!');
      setSelectValue(initialValue);
      setSelectedDoctor(null);
      setSelectedDate('');
      setSelectTime('');
      setCurrent(0);
      dispatch(addInvoice({ ...appointmentData }));

      const goToSuccess = () => navigate(`/booking/success/${appointmentData.id}`);
      Modal.success({
        className: 'appointment-success-modal',
        title: 'Booking request received',
        content: 'Your requested time has been sent to our team. We will coordinate with the specialist and contact you to confirm the final appointment time and arrange payment. Details have been sent to your email.',
        okText: 'View booking',
        onOk: goToSuccess,
      });
      const t = setTimeout(goToSuccess, 4000);
      return () => clearTimeout(t);
    }
    if (isError) {
      message.error(error?.data?.message || 'Something went wrong');
    }
  }, [isSuccess, isError, appointmentData, error, dispatch, navigate]);

  const handleDateChange = (date) => setSelectedDate(moment(date).format('YYYY-MM-DD HH:mm:ss'));

  const steps = [
    {
      title: 'Select Doctor',
      content: (
        <SelectDoctor selectedDoctor={selectedDoctor} onSelect={setSelectedDoctor} onConfirm={(doc) => { setSelectedDoctor(doc); next(); }} />
      ),
    },
    {
      title: 'Date & Time',
      content: (
        <SelectApppointment
          handleDateChange={handleDateChange}
          selectedDate={selectedDate}
          selectTime={selectTime}
          setSelectTime={setSelectTime}
          selectedDoctor={selectedDoctor}
        />
      ),
    },
    {
      title: 'Patient Information',
      content: (
        <PersonalInformation
          handleChange={handleChange}
          selectValue={selectValue}
          setPatientId={setPatientId}
        />
      ),
    },
    {
      title: 'Confirm',
      content: (
        <CheckoutPage
          handleChange={handleChange}
          selectValue={selectValue}
          isCheck={isCheck}
          setIsChecked={setIsChecked}
          data={selectedDoctor || false}
          selectedDate={selectedDate}
          selectTime={selectTime}
          onCaptchaToken={setCaptchaToken}
        />
      ),
    },
  ];

  const items = steps.map((item) => ({ key: item.title, title: item.title }));

  const canProceedStep0 = !!selectedDoctor;
  // Date and time are optional preferences; the team confirms the real slot.
  const canProceedStep1 = true;

  return (
    <>
      <Header />
      <div className="appointment-page">
        <div className="container container-wrap">
          <Steps current={current} items={items} />
          <div className="appointment-step">{steps[current].content}</div>
          <div className="appointment-actions">
            <div>
              {current > 0 && (
                <Button type="default" size="large" onClick={prev}>
                  Previous
                </Button>
              )}
            </div>
            <div>
              {current < steps.length - 1 && (
                <Button
                  type="primary"
                  size="large"
                  disabled={
                    current === 0
                      ? !canProceedStep0
                      : current === 1
                        ? !canProceedStep1
                        : IsDisable
                  }
                  onClick={next}
                >
                  Next
                </Button>
              )}
              {current === steps.length - 1 && (
                <Button
                  type="primary"
                  size="large"
                  disabled={isConfirmDisable}
                  loading={isLoading}
                  onClick={handleConfirmSchedule}
                >
                  Confirm & schedule
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default AppointmentPage;
